import { KeyboardEvent, useRef } from 'react';
import { PillToggle, SIZE_L, SIZE_S } from '@components/pill-toggle';
import classNames from 'classnames';

import {
  PILL_TOGGLE_GROUP_VARIANT_PLAIN_TABS,
  PILL_TOGGLE_GROUP_VARIANT_SEGMENTED,
  PILL_TOGGLE_GROUP_VARIANT_TABS,
  PillToggleGroupProps,
} from './pill-toggle-group.type';

export const PillToggleGroup = <T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  size = SIZE_L,
  variant = PILL_TOGGLE_GROUP_VARIANT_SEGMENTED,
  fullWidth = false,
  backgroundClassName = 'bg-surface-secondary',
  textClassName,
  ariaLabel,
  className,
}: PillToggleGroupProps<T>) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectAt = (index: number) => {
    const wrapped = ((index % options.length) + options.length) % options.length;
    onChange(options[wrapped].value);
    buttonRefs.current[wrapped]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        selectAt(currentIndex + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        selectAt(currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        selectAt(0);
        break;
      case 'End':
        event.preventDefault();
        selectAt(options.length - 1);
        break;
      default:
    }
  };

  const handleClick = (next: T) => {
    if (disabled) return;
    onChange(next);
  };

  const isPlainTabs = variant === PILL_TOGGLE_GROUP_VARIANT_PLAIN_TABS;

  return (
    <div
      role='radiogroup'
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={classNames(
        'items-center rounded-md',
        backgroundClassName,
        fullWidth ? 'flex w-full' : 'inline-flex',
        {
          'gap-px p-0.5': variant === PILL_TOGGLE_GROUP_VARIANT_SEGMENTED,
          'gap-1 xs:gap-2 p-1': variant === PILL_TOGGLE_GROUP_VARIANT_TABS || isPlainTabs,
          'opacity-50': disabled,
        },
        className
      )}>
      {options.map((option, index) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            type='button'
            role='radio'
            aria-checked={isSelected}
            aria-disabled={disabled || undefined}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => handleClick(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={classNames(
              'inline-flex items-center justify-center gap-1 rounded-[5px]',
              'transition-[background-color] duration-50 ease-out',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              {
                'h-9': size === SIZE_S,
                'h-[42px]': size === SIZE_L,
                'cursor-not-allowed': disabled,
                'flex-1 min-w-0': fullWidth,
              },
              // Plain tabs render every state on the button itself — no PillToggle and no `fill-*`
              // utility (those bake in a hover that mimics the selected pill). Selected gets a flat
              // fill, unselected stays fully transparent.
              isPlainTabs && [
                textClassName ?? 'text-subtitle-m',
                !fullWidth && 'px-3',
                isSelected ? 'bg-neutral-3 dark:bg-neutral-70 text-primary' : 'text-tertiary',
              ],
              // Other variants delegate the SELECTED pill to <PillToggle>, so only the unselected button needs styling.
              !isPlainTabs &&
                !isSelected && [
                  variant === PILL_TOGGLE_GROUP_VARIANT_TABS
                    ? ['fill-secondary text-tertiary', textClassName ?? 'text-subtitle-m']
                    : ['text-secondary', textClassName ?? 'text-body-m'],
                  !fullWidth && 'px-3',
                ]
            )}>
            {!isPlainTabs && isSelected ? (
              <PillToggle
                variant={option.variant}
                label={option.label}
                size={size}
                indicator={option.indicator}
                fullWidth={fullWidth}
                textClassName={textClassName}
              />
            ) : (
              <>
                {fullWidth ? <span className='truncate'>{option.label}</span> : option.label}
                {option.indicator}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};
